import {
  clearConversationV2Cache,
  loadConversationCategories as loadConversationCategoriesV2,
  loadConversationDocument,
} from './conversationV2';
import { loadGrammarManifest, loadGrammarSections, clearGrammarCache } from './grammarV2';

// homeLang is unused: conversations are English-anchored (see
// conversationV2.js), not per-home-language, so there's nothing left for it
// to select — kept only so callers don't need to change.
export function loadConversationCategories(homeLang, locale) {
  return loadConversationCategoriesV2(locale);
}

// grammar/catalog.json no longer carries a "category" field (see
// grammarV2.js) — this is the same 13-lesson set every locale's catalog used
// to tag category: 'tense_aspect' (tense/aspect ids are portable, shared
// across every locale's curriculum), hardcoded here since Practice's tense
// sampling is the only thing that needs to slice the catalog this way.
const TENSE_ASPECT_LESSON_IDS = new Set([
  'present_simple',
  'present_continuous',
  'present_perfect',
  'present_perfect_continuous',
  'past_simple',
  'past_continuous',
  'past_perfect',
  'past_perfect_continuous',
  'future_simple',
  'future_continuous',
  'future_perfect',
  'future_perfect_continuous',
  'past_habits',
]);

export function conversationToPracticeData(chapter) {
  const speakers = new Map((chapter.speakers || []).map((speaker) => [speaker.id, speaker.displayName]));
  const content = {};
  (chapter.lines || []).forEach((line) => {
    if (line.type !== 'dialogue') return;
    const spoken = (line.segments || [])
      .filter((segment) => segment.type === 'speech' && segment.text)
      .map((segment) => segment.text)
      .join(' ')
      .trim();
    if (!spoken) return;
    const speaker = speakers.get(line.speakerId);
    content[line.id] = speaker ? `${speaker}: ${spoken}` : spoken;
  });
  return { id: chapter.conversationId, label: chapter.title || '', content };
}

// homeLang is unused — see loadConversationCategories above.
export async function loadConversationChapter(homeLang, locale, conversationId) {
  return conversationToPracticeData(await loadConversationDocument(locale, conversationId));
}

export function grammarLessonToPracticeTense(lesson, document) {
  const section = (document.sections || []).find((item) => item.id === lesson.id)
    || document.sections?.[0];
  if (!section) return null;
  const rows = (section.examples || []).map((example) => ({
    person: example.tokens?.subject || example.id,
    form: example.tokens?.verb || example.tokens?.linkingVerb || example.text,
  })).filter((row) => row.person && row.form);
  return {
    name: section.label || lesson.label || lesson.id.replaceAll('_', ' '),
    use: section.concept || '',
    verb: section.patterns?.[0]?.form || '',
    rows,
    examples: (section.examples || []).map((example) => example.text).filter(Boolean),
  };
}

export async function loadGrammarPractice(homeLang) {
  const [manifest, sections] = await Promise.all([
    loadGrammarManifest(homeLang),
    loadGrammarSections(homeLang),
  ]);
  const lessons = manifest.filter((lesson) => TENSE_ASPECT_LESSON_IDS.has(lesson.id));
  return lessons
    .map((lesson) => grammarLessonToPracticeTense(lesson, sections))
    .filter(Boolean);
}

export function clearPracticeV2Cache() {
  clearConversationV2Cache();
  clearGrammarCache();
}
